import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { toNumber } from 'lodash';

import Permissions from '@/common/permissions';
import useHasAccess from '@/modules/core/hooks/useHasAccess';
import { PaymentMethodType } from '@/modules/employee/employee.types';
import {
  useCommunicationCategoryStore,
  useCommunicationChannelStore,
  useStatementsSettingsStore,
} from '@/modules/employer/components/SetupEmployer/Communication/stores';
import useMultiFactorAuthenticationStore from '@/modules/employer/components/SetupEmployer/ContactsSecurity/MultiFactorAuthentication/stores/useMultiFactorAuthentication.store';
import useAIAdjudicationStore from '@/modules/employer/components/SetupEmployer/Features/AIAdjudication/stores/useAIAdjudication.store';
import useFraudThresholdStore from '@/modules/employer/components/SetupEmployer/Features/FraudThreshold/stores/useFraudThreshold.store';
import usePlansRenewalStore from '@/modules/employer/components/SetupEmployer/Features/PlansRenewal/stores/usePlansRenewal.store';
import useReimbursementStore from '@/modules/employer/components/SetupEmployer/Features/Reimbursement/stores/useReimbursement.store';
import useFileManagerSettingsStore from '@/modules/employer/components/SetupEmployer/FilesAndImports/FileManagerSettings/stores/useFileManagerSettings.store';
import { useAccountUsageStore } from '@/modules/employer/components/SetupEmployer/FinancialDetails/BankInformation/AccountUsage/stores';
import useBanksListQuery from '@/modules/employer/components/SetupEmployer/FinancialDetails/BankInformation/BanksList/useBanksList.query';
import useLinkManagementStore from '@/modules/employer/components/SetupEmployer/LinkManagement/useLinkManagementStore.store';
import { useActivateEmployerMutation } from '@/modules/employer/components/SetupEmployer/queries/useActivateEmployer.query';
import { useTechnicalSettingsStore } from '@/modules/employer/components/SetupEmployer/TechnicalSettings/stores/useTechnicalSettings.store';
import useGetEmployer from '@/modules/employer/hooks/useGetEmployer.query';
import { useSetupEmployerStore } from '@/modules/employer/store';

import { useCustomReportsStore } from '../AdminDashboard/CustomReports';
import { useMetricsStore } from '../AdminDashboard/Metrics/stores';
import useClaimsSettingsStore from '../ClaimsSettings/store/useClaimsSettings.store';
import { useEmailConfigurationStore } from '../Communication/EmailConfiguration/stores';
import { useExpressSetupLinksStore } from '../Features/ExpressSetupLinks/stores';
import { useOrphanConfigurationStore } from '../Features/OrphanConfiguration/stores';
import useMobileAppCustomizationStore from '../MobileAppCustomization/store/useMobileAppCustomization.store';
import { useSaveEmployerQuery } from '../queries';
import { generalInformationPayload } from '../queries/useSaveEmployer.query';

export const HARD_TO_REPRODUCE_ERROR_MESSAGE = 'Not enough data to save.';

const useSaveEmployer = () => {
  const claimsSettings = useClaimsSettingsStore();
  const { state: reimbursementState, paymentEmployeeFees } = useReimbursementStore();
  const store = useSetupEmployerStore();
  const orphanConfigurationStore = useOrphanConfigurationStore((state) => state.state);
  const expressSetupLinksStore = useExpressSetupLinksStore((state) => state.state);

  const accountUsageStore = useAccountUsageStore();
  const technicalSettingsStore = useTechnicalSettingsStore();
  const fileManagerSettingsState = useFileManagerSettingsStore((state) => state.state);
  const communicationChannelStore = useCommunicationChannelStore((state) => state.state);
  const communicationCategoryStore = useCommunicationCategoryStore();
  const statementsSettingsStore = useStatementsSettingsStore((state) => state.state);
  const emailConfigurationState = useEmailConfigurationStore((state) => state.state);
  const linkManagementStore = useLinkManagementStore((state) => state.state);
  const { state: aiAdjudicationStore } = useAIAdjudicationStore();
  const { state: fraudThresholdStore } = useFraudThresholdStore();
  const { state: plansRenewalStore } = usePlansRenewalStore();
  const { state: multiFactorAuthenticationStore } = useMultiFactorAuthenticationStore();
  const mobileAppCustomizationState = useMobileAppCustomizationStore((state) => state.state);
  const { id } = useParams<{ id: string }>();
  const { save } = useSaveEmployerQuery(id);
  const { formattedData: employerData } = useGetEmployer();
  const { activateEmployerAsync } = useActivateEmployerMutation();
  const { banks } = useBanksListQuery(id);
  const hasAccessToBankInfo = useHasAccess([{ permission: Permissions.EDIT_ER_BANK_ACCOUNT_INFO }]);
  const allowedPaymentMethods = useMemo(
    () =>
      reimbursementState.allowedPaymentMethods || employerData?.overridableProperties?.allowedPaymentMethods,
    [employerData.overridableProperties, reimbursementState.allowedPaymentMethods]
  );
  const reimbursementPayload = useMemo(() => {
    if (allowedPaymentMethods) {
      return {
        is_reimbursement_allowed:
          allowedPaymentMethods.length === 0 ? false : reimbursementState.isReimbursementAllowed,
        ...(reimbursementState.isReimbursementAllowed
          ? {
              allowed_payment_method_configs: reimbursementState?.allowedPaymentMethods?.map(
                (paymentMethodType) => ({
                  payment_method_type: paymentMethodType,
                  payment_employee_fee: toNumber(paymentEmployeeFees?.[paymentMethodType]) || null,
                })
              ),
            }
          : {}),
        is_bill_pay_allowed: reimbursementState.isBillPayAllowed,
        is_claim_offsets_allowed: reimbursementState.isClaimOffsetsAllowed,
        ...(typeof reimbursementState?.claimOffsetLogic === 'string' && reimbursementState.isClaimOffsetsAllowed
          ? {
              claim_offset_logic: reimbursementState.claimOffsetLogic,
            }
          : {}),
        ...(reimbursementState?.minimumPaymentReimbursementAmount
          ? {
              minimum_payment_reimbursement_amount: reimbursementState?.minimumPaymentReimbursementAmount,
            }
          : {}),
        ...(reimbursementState?.waitingForFundsContinuePayment
          ? {
              waiting_for_funds_continue_payment: reimbursementState?.waitingForFundsContinuePayment,
            }
          : {}),
        ...(reimbursementState?.waitingForFundsExpiration
          ? {
              waiting_for_funds_expiration: reimbursementState?.waitingForFundsExpiration,
            }
          : {}),
      };
    }

    return {
      is_reimbursement_allowed:
        employerData.overridableProperties?.allowedPaymentMethods?.length === 0
          ? false
          : employerData.overridableProperties?.isReimbursementAllowed,
      ...(employerData.overridableProperties?.isReimbursementAllowed
        ? {
            allowed_payment_method_configs:
              employerData.overridableProperties?.allowedPaymentMethodConfigs?.map((item) => ({
                payment_method_type: item?.paymentMethodType as PaymentMethodType,
                payment_employee_fee: toNumber(item?.paymentEmployeeFee) || null,
              })),
          }
        : {}),
      is_bill_pay_allowed: employerData.overridableProperties?.isBillPayAllowed,
    };
  }, [reimbursementState, employerData.overridableProperties, allowedPaymentMethods, paymentEmployeeFees]);
  const reportsState = useCustomReportsStore((state) => state.state);
  const metricsState = useMetricsStore((state) => state.state);

  const formattedPayload = useMemo(() => {
    const payload = generalInformationPayload(
      banks,
      store.generalInfo,
      store.address,
      store.employer,
      communicationChannelStore,
      communicationCategoryStore.state,
      accountUsageStore.state,
      employerData,
      store.employeeId,
      store.fundingModel,
      store.cardsSetup,
      hasAccessToBankInfo,
      reimbursementPayload,
      store.helpInfo,
      store.primaryContactId,
      technicalSettingsStore.state,
      fileManagerSettingsState,
      emailConfigurationState,
      statementsSettingsStore,
      aiAdjudicationStore,
      multiFactorAuthenticationStore,
      mobileAppCustomizationState,
      claimsSettings.state,
      orphanConfigurationStore,
      linkManagementStore,
      expressSetupLinksStore,
      {
        reports: reportsState,
        metrics: metricsState,
      },
      plansRenewalStore,
      fraudThresholdStore
    );
    return payload;
  }, [
    banks,
    communicationChannelStore,
    communicationCategoryStore.state,
    accountUsageStore.state,
    employerData,
    store.address,
    store.employer,
    store.primaryContactId,
    store.generalInfo,
    store.employeeId,
    store.helpInfo,
    store.fundingModel,
    store.cardsSetup,
    hasAccessToBankInfo,
    reimbursementPayload,
    technicalSettingsStore.state,
    fileManagerSettingsState,
    emailConfigurationState,
    statementsSettingsStore,
    aiAdjudicationStore,
    multiFactorAuthenticationStore,
    mobileAppCustomizationState,
    linkManagementStore,
    plansRenewalStore,
    claimsSettings.state,
    orphanConfigurationStore,
    expressSetupLinksStore,
    reportsState,
    metricsState,
    fraudThresholdStore,
  ]);
  const handleActivateEmployerAsync = useCallback(
    async (organizationId: string) => {
      await activateEmployerAsync(organizationId);
    },
    [activateEmployerAsync]
  );

  const handleSave = useCallback(async () => {
    if (formattedPayload?.normal_properties?.external_account_enable &&
      !formattedPayload?.normal_properties?.tax_id &&
      !formattedPayload?.normal_properties?.invoicing_contact_id &&
      !formattedPayload?.normal_properties?.account_manager_id &&
      !formattedPayload?.normal_properties?.external_administrator_fees_account &&
      !formattedPayload?.normal_properties?.external_payroll_contributions_account &&
      !formattedPayload?.normal_properties?.external_replenishment_account) {
      throw new Error(HARD_TO_REPRODUCE_ERROR_MESSAGE);
    }

    return save(formattedPayload);
  }, [formattedPayload, save]);
  return {
    handleSave,
    handleActivateEmployerAsync,
  };
};

export default useSaveEmployer;
